import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bot, 
  Send, 
  User, 
  Sparkles, 
  CornerDownRight, 
  CheckCircle2, 
  AlertCircle, 
  ShieldAlert, 
  Search, 
  RefreshCw,
  ArrowDown
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Built-in Knowledge Base for AskProf
export const AskProfKnowledgeBase = [
  // =========================================================================
  // 1. GREETINGS & IDENTITY
  // =========================================================================
  {
    category: "greetings",
    keywords: ['hi', 'hello', 'hey', 'greetings', 'good morning', 'good afternoon', 'good evening', 'who are you', 'what is your name', 'AskProf'],
    answer: "Hello! I am AskProf, the dedicated academic and research assistant for Prof. Sanjay Dhir. I have comprehensive knowledge of his academic career, teaching curriculum, doctoral supervision, research methodologies (TISM/M-TISM), journal publications, books, case studies, patents, and policy advisory roles. How can I assist you today?"
  },
  {
    category: "biography_core",
    keywords: ['who is', 'about sanjay dhir', 'biography', 'profile', 'sanjay dhir', 'designation', 'position', 'role', 'title', 'qualification', 'education', 'background'],
    answer: "Prof. Sanjay Dhir is a Professor and Area Chair of the Strategic Management Area at the Department of Management Studies (DMS), Indian Institute of Technology (IIT) Delhi. He completed his Fellow Programme in Management (FPM / Ph.D.) from the Indian Institute of Management (IIM) Lucknow. Prior to his academic tenure, he worked for over three years in the Research & Development Department at Mahindra & Mahindra Ltd. (Automotive Division). At IIT Delhi, he serves on the Institute Senate, Institute Ranking Committee, and coordinates the Doctoral Programme."
  },

  // =========================================================================
  // 2. CONTACT & ADMINISTRATIVE DETAILS
  // =========================================================================
  {
    category: "contact_details",
    keywords: ['contact', 'email', 'phone', 'telephone', 'mobile', 'address', 'room', 'office', 'where', 'chamber', 'location', 'appointment', 'meet'],
    answer: "Prof. Sanjay Dhir's contact coordinates:\n• Official Email: sanjaydhir@dms.iitd.ac.in\n• Alternative Research Email: sanjaydhir.iitd@gmail.com\n• Office Phone: +91-11-26596386\n• Office Chambers: IV Floor, Vishwakarma Bhavan, Department of Management Studies, IIT Delhi, Hauz Khas, New Delhi – 110016, India.\nNote: In-person visits and discussions are scheduled strictly by prior email appointment."
  },
  {
    category: "academic_identifiers",
    keywords: ['scopus', 'google scholar', 'orcid', 'publons', 'researchgate', 'citations', 'h-index', 'profiles'],
    answer: "Prof. Sanjay Dhir's academic metrics and verified persistent identifiers:\n• Scopus Author ID: 56966376600\n• ORCID Digital Identifier: 0000-0001-6754-9602\n• Google Scholar: user=1ntflp8AAAAJ\n• Publons / Web of Science: researcher/4818681/sanjay-dhir/\n• ResearchGate Profile: Sanjay-Dhir-3"
  },

  // =========================================================================
  // 3. RESEARCH DOMAINS & METHODOLOGIES
  // =========================================================================
  {
    category: "research_domains",
    keywords: ['research', 'research interest', 'domains', 'area of interest', 'specialization', 'topics', 'coopetition', 'ambidexterity', 'flexibility'],
    answer: "Prof. Dhir's empirical research is concentrated across two primary tracks:\n1. Strategic Capability & Innovation: Strategic thinking enablers, organizational ambidexterity, strategic flexibility, inter-partner dynamics in joint ventures, coopetition strategy, and digital transformation.\n2. Public Systems & Healthcare Logistics: Healthcare delivery equity, vaccine supply chain temperature monitoring, mobile medical clinics, oxygen distribution infrastructure, and anti-microbial resistance stewardship.\nMethodologically, his laboratory specializes in Total Interpretive Structural Modeling (TISM), Modified TISM (M-TISM), MICMAC analysis, Partial Least Squares Structural Equation Modeling (PLS-SEM), and text-mining-enabled technology roadmapping."
  },
  {
    category: "tism_methodology",
    keywords: ['tism', 'm-tism', 'total interpretive structural modeling', 'micmac', 'interpretive structural', 'structural model'],
    answer: "Total Interpretive Structural Modeling (TISM) and Modified TISM (M-TISM) combined with MICMAC analysis are specialized qualitative-quantitative structural modeling methodologies championed in Prof. Dhir's publications. They are used to identify direct, transitive, and polarity-driven relationships among complex interdependent factors—such as institutional enablers for national innovation systems, cyber-security governance, and vaccine campaign logistics."
  },

  // =========================================================================
  // 4. TEACHING & COURSES
  // =========================================================================
  {
    category: "courses_taught",
    keywords: ['course', 'courses', 'teaching', 'subject', 'teach', 'class', 'classes', 'syllabus', 'pedagogy', 'curriculum', 'lecture', 'msl 703'],
    answer: "At the Department of Management Studies (DMS), IIT Delhi, Prof. Dhir delivers 11 specialized MBA, Executive, and Doctoral courses:\n1. Strategic Management (Core)\n2. Management of Innovation and R&D\n3. Management of Technology Transfer and Absorption (Course Code: MSL 703)\n4. Creative Problem Solving (TRIZ Methodology)\n5. Fundamentals of Management of Technology\n6. Strategic Consulting\n7. Consulting Toolkit\n8. Strategic Alliance\n9. Current and Emerging Issues in Strategic Management\n10. International Business\n11. Small Business Strategy"
  },
  {
    category: "teaching_awards",
    keywords: ['teaching award', 'professor of the year', 'insideiim', 'teaching excellence', 'pedagogy award'],
    answer: "Prof. Sanjay Dhir has received the prestigious IIT Delhi Teaching Excellence Award and was voted 'InsideIIM Professor of the Year' at IIT Delhi for two consecutive years (2020 and 2021) in recognition of his case-driven pedagogy and mentorship."
  },

  // =========================================================================
  // 5. INDUSTRIAL PATENT
  // =========================================================================
  {
    category: "patent",
    keywords: ['patent', 'patents', 'invention', 'scorpio', 'mahindra', 'suspension', 'suspension link', 'automotive r&d'],
    answer: "Prof. Sanjay Dhir holds an industrial engineering patent with Mahindra & Mahindra Ltd. (Automotive Division):\n• Title: Suspension Link for Scorpio Refresh SUV\n• Filing / Published: April 2007 (04/2007)\n• Scope: Structural load-bearing optimization, unsprung mass reduction, and enhanced all-terrain ride dynamics for the Scorpio platform."
  },

  // =========================================================================
  // 6. HARVARD & IVEY CASE STUDIES
  // =========================================================================
  {
    category: "case_studies",
    keywords: ['case study', 'case studies', 'ivey', 'harvard', 'duckduckgo', 'educomp', 'berrylush', 'disney', 'himbunkar'],
    answer: "Prof. Dhir authors applied empirical teaching cases published by Richard Ivey Publishing and distributed worldwide through Harvard Business Publishing:\n• 'Berrylush: Scaling for Growth' (Emerald Emerging Markets Case Studies, 2024)\n• 'DUCKDUCKGO – Reinventing the Internet Search' (Ivey Case Series / Harvard Business Publishing, Product #W19276-PDF-ENG, 2019)\n• 'Educomp – Shaping Education in the New Millennium' (Ivey Case Series / Harvard Business Publishing, Product #W13318-PDF-ENG, 2013)\n• 'The Walt Disney: Strategic Acquisition for Achieving Creativity' (Cases in Management – Economic Times, 2015)\n• 'Himbunkar: Turnaround of a Social Public Sector Enterprise' (South Asian Journal of Management, 2016)"
  },

  // =========================================================================
  // 7. PUBLISHED BOOKS & BOOK CHAPTERS
  // =========================================================================
  {
    category: "books_and_chapters",
    keywords: ['book', 'books', 'springer', 'book chapter', 'book chapters', 'mcgraw hill', 'authored books'],
    answer: "Prof. Dhir has co-edited and authored scholarly books and chapters with Springer Nature and other international publishers:\n• Book Chapter (2024): 'The Influence of Sustainable Technology Transfer and Sustainable Innovation on Green Growth' (Springer Proceedings in Business and Economics)\n• Book Chapter (2018): 'MSME Competitiveness for the Global Value Chain—A TRIZ-Based Approach' (Springer)\n• Book Chapter (2018): 'Technology Transfer and Innovation in Global International Joint Ventures' (Springer)\n• Book Chapter (2017): 'Lakshya Food (India) Limited: Growth and Expansion Strategy' (The Case Centre)\n• Book Chapter (2016): 'Global Competitiveness of Informal Economy Organizations' (Springer)\n• Books focus on Flexible Systems Management, Strategic Alliances, and Global Value Chains."
  },

  // =========================================================================
  // 8. REPRESENTATIVE JOURNAL PUBLICATIONS
  // =========================================================================
  {
    category: "journal_publications",
    keywords: ['paper', 'papers', 'journal', 'journals', 'journal publications', 'ieee', 'ijpe', 'emerald', 'sciencedirect', 'vaccine paper'],
    answer: "Prof. Dhir has authored over 100 peer-reviewed journal papers. Selected flagship articles include:\n• (2024, IJPE): 'Coopetition, strategy, and business performance in the era of digital transformation using a multi-method approach'\n• (2024, JIK): 'Characterizing generative artificial intelligence applications: Text-mining-enabled technology roadmapping'\n• (2024, GJFSM): 'Critical success factors for emerging technology adoption, strategic flexibility, and competitiveness: TISM-E'\n• (2024, GJFSM): 'Ensuring Vaccine Temperature Integrity: Monitoring from Storage to Last-Mile Delivery'\n• (2021, IEEE TEM): 'Organizational Ambidexterity: A Review and Research Agenda'\n• (2021, TFSC): 'Developing a modified total interpretive structural model (M-TISM) for organizational strategic cybersecurity management'\n• (2020, JBR): 'Integration of knowledge and enhancing competitiveness: Acquisition of Zain by Bharti Airtel'"
  },

  // =========================================================================
  // 9. CONFERENCES & GLOBAL PROCEEDINGS
  // =========================================================================
  {
    category: "conferences",
    keywords: ['conference', 'conferences', 'aom', 'sms', 'bam', 'aib', 'ice-iamot', 'glogift', 'proceedings'],
    answer: "Prof. Dhir's work has been presented across 59+ peer-reviewed proceedings and international symposia, including:\n• Academy of Management (AoM) Annual Meetings (Chicago, Briarcliff Manor)\n• Strategic Management Society (SMS) Conferences (Glasgow, Singapore, Hong Kong)\n• British Academy of Management (BAM) (Manchester, London, Birmingham)\n• Academy of International Business (AIB) Global & India Chapters\n• IEEE ICE / IAMOT & GLOGIFT Conferences"
  },

  // =========================================================================
  // 10. SPONSORED PROJECTS & POLICY ADVISORY
  // =========================================================================
  {
    category: "projects_and_policy",
    keywords: ['project', 'projects', 'gates foundation', 'bill & melinda gates', 'aarogya setu', 'oxygen', 'ntag', 'delhi high court', 'gem', 'ministry'],
    answer: "Prof. Dhir has served as Principal Investigator or lead consultant on major government and international health initiatives:\n• COVID-19 Vaccine Rollout: Institutional management evaluation in partnership with the Bill & Melinda Gates Foundation (BMGF).\n• Medical Oxygen Infrastructure: Member of the National Technical Advisory Group (NTAG) Expert Committee for Government of India and nominated expert by the Hon'ble Delhi High Court / GNCTD during the medical oxygen logistics crisis.\n• Aarogya Setu App: Advisory inputs for adoption and operationalization under the Ministry of Health and Family Welfare.\n• Government e-Marketplace (GeM): Comprehensive procurement impact assessment for the Ministry of Commerce & Industry."
  },

  // =========================================================================
  // 11. WORKSHOPS & STRATEGIC ROUNDTABLES
  // =========================================================================
  {
    category: "workshops_roundtables",
    keywords: ['workshop', 'workshops', 'roundtable', 'roundtables', 'clinton foundation', 'amr', 'cervical cancer', 'sabin', 'empower school', 'jivika'],
    answer: "Prof. Dhir has organized and moderated national roundtables and global dissemination summits:\n• Anti-Microbial Resistance (AMR): Roundtable in collaboration with the William J. Clinton Foundation (Dec 2023)\n• Cervical Cancer Elimination in India: Two-day National Consortium roundtable with Sabin Vaccine Institute (April 2024)\n• India-Africa Capacity Building: Medical products manufacturing roundtable with Empower School of Health (Dec 2023)\n• Mobile Clinics in Public Healthcare: Dissemination summit with Jivika Healthcare (May 2023)\n• Climate Change Policy: Research dissemination on MARCH with Nangia Co. LLP (July 2024)"
  },

  // =========================================================================
  // 12. PH.D. SUPERVISION & DOCTORAL ALUMNI
  // =========================================================================
  {
    category: "phd_supervision_general",
    keywords: ['phd', 'ph.d', 'doctoral', 'supervision', 'supervise', 'guidance', 'join phd', 'apply for phd', 'research scholar'],
    answer: "Prof. Dhir supervises PhD researchers at DMS, IIT Delhi, and interdisciplinary fellows at the University of Queensland–IIT Delhi Research Academy (UQIDAR). His researchers work on AI governance, technology adoption, public healthcare equity, endowment funds, and strategic alliances. Candidates interested in PhD admissions must apply formally through the IIT Delhi postgraduate admission portal during official intake cycles (Semester I and Semester II)."
  },
  {
    category: "phd_alumni_placements",
    keywords: ['alumni', 'phd alumni', 'former students', 'graduated scholars', 'nisha bamel', 'shishir shrotriya', 'nakul parameshwar', 'shiwangi singh', 'jan e alam'],
    answer: "Graduated doctoral alumni under Prof. Sanjay Dhir hold distinguished appointments across academia, government, and defence:\n• Dr. Jan E Alam: Chief Secretary, Government of Nagaland (IAS)\n• Dr. Shishir Shrotriya: Commodore, Indian Navy\n• Dr. Nakul Parameshwar: Faculty, IIT Hyderabad\n• Dr. Shiwangi Singh: Faculty, IIM Visakhapatnam\n• Dr. Aqueeb Sohail Shaik: Faculty, IIM Sambalpur\n• Dr. Kamakshi Sharma: Faculty, IIM Jammu\n• Dr. Nisha Bamel: Faculty, BIMTECH\n• Dr. Mukesh Jain: Senior Civil Servant (IPS Officer) & Visiting Faculty, Mahindra University\n• Dr. Ishita Batra: Faculty, FORE School of Management\n• Dr. Shashank Shekhar: Indian Biological Data Centre (IBDC)\n• Dr. Rishabh Rajan: Faculty, CIMP\n• Dr. Mansi Singh: Faculty, Jindal Global Business School\n• Dr. Kumari Surabhi Singh: Faculty, IIM Bodh Gaya\n• Dr. Rubina Chakma: Faculty, IIM Rohtak\n• Dr. Pooja: Faculty, Jindal Global Business School\n• Dr. Abhilasha: Faculty, University of Delhi\n• Dr. Harchitwan Kaur Lamba: Industry Consultant"
  },

  // =========================================================================
  // 13. CURRENT DOCTORAL SCHOLARS
  // =========================================================================
  {
    category: "current_scholars",
    keywords: ['current scholars', 'current phd', 'pursuing scholars', 'shruti acharya', 'nehal jain', 'wungkhai', 'muskan verma', 'siya choudhary', 'sanjna shukla', 'navya tondak'],
    answer: "Current doctoral researchers working under Prof. Dhir's supervision at IIT Delhi and UQIDAR include:\n• Shruti Acharya: Vaccine barriers and public health delivery\n• Nehal Jain: Equity and inclusivity in digital health organizations\n• Wungkhai Hommichon: Clinician resistance to AI in cancer detection\n• Muskan Verma: Strategic entrepreneurship and strategic renewal\n• Siya Choudhary: Generative AI implementation in enterprise operations\n• Sanjna Shukla: AI scribe technology for clinical documentation (UQIDAR)\n• Dr. Navya Tondak: Autonomous AI and organizational governance\n• Suman Kumar: Cross-border alliances & ambidexterity in emerging markets\n• Suvashis Choudhary: AI adoption for enterprise cybersecurity\n• Arun Singh: Strategic flexibility and modular operational design\n• Ayesha Kutay: Higher education endowment fund resilience\n• Priyanka: Innovation dynamics in post-alliance governance\n• Juhi Rajput: Climate governance and corporate net-zero roadmaps\n• Pushpendra Rajput: Multi-partner alliance networks and portfolio theory\n• Radhika Goel: Inclusiveness and quality delivery in healthcare"
  }
];

export default function AskMe() {
  // Chatbot State
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: "Hello! I am AskProf, Prof. Sanjay Dhir's academic assistant. Ask me anything about his research, courses, patents, or mentorship. If I don't know the answer, you can leave a comment below for him to reply directly!"
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);

  // Q&A / Comments State
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState({ loading: false, success: false, error: '' });

  const commentSectionRef = useRef(null);

  // Auto-scroll helper (called ONLY when AskProf has no answer or user clicks button)
  const scrollToComments = () => {
    if (commentSectionRef.current) {
      commentSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Reset scroll to top on page load/refresh
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Fetch comments/questions
  const fetchQuestions = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ask-me`);
      if (res.ok) {
        const data = await res.json();
        setQuestions(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('Backend currently offline, waiting for server connection:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  // Keep chat feed scrolled to bottom internally without hijacking page scroll
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages, isTyping]);

  // Chatbot Response Engine
  const handleChatSubmit = (e) => {
    e.preventDefault();
    const query = chatInput.trim();
    if (!query) return;

    // Append User Message
    const userMsg = { sender: 'user', text: query };
    setMessages((prev) => [...prev, userMsg]);
    setChatInput('');
    setIsTyping(true);

    setTimeout(() => {
      const cleanQuery = query.toLowerCase().replace(/[?.,!]/g, ' ');
      const queryTokens = cleanQuery.split(/\s+/).filter(Boolean);

      let bestMatch = null;
      let highestScore = 0;

      for (const item of AskProfKnowledgeBase) {
        let score = 0;
        for (const kw of item.keywords) {
          const lowerKw = kw.toLowerCase();
          if (cleanQuery.includes(lowerKw)) {
            score += lowerKw.includes(' ') ? 8 : 4;
          } else {
            const kwTokens = lowerKw.split(/\s+/);
            const matchedTokens = kwTokens.filter((token) => queryTokens.includes(token));
            if (matchedTokens.length === kwTokens.length) {
              score += kwTokens.length * 3;
            } else if (matchedTokens.length > 0 && kwTokens.length > 1) {
              score += matchedTokens.length;
            }
          }
        }

        if (score > highestScore) {
          highestScore = score;
          bestMatch = item;
        }
      }

      if (bestMatch && highestScore >= 3) {
        setMessages((prev) => [
          ...prev,
          { sender: 'bot', text: bestMatch.answer }
        ]);
      } else {
        // Fallback: AskProf has no answer -> Notify and smoothly scroll down to comments
        setMessages((prev) => [
          ...prev,
          {
            sender: 'bot',
            text: "I do not have a verified answer for this specific query in my knowledge base. Scrolling down to the comments section so you can submit your inquiry directly to Prof. Sanjay Dhir...",
            isFallback: true
          }
        ]);
        setTimeout(() => {
          scrollToComments();
        }, 400);
      }
      setIsTyping(false);
    }, 550);
  };

  // Submit Community Comment
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !question.trim()) return;

    setStatus({ loading: true, success: false, error: '' });
    try {
      const res = await fetch(`${API_BASE_URL}/api/ask-me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, question }),
      });

      const data = await res.json();
      if (res.ok) {
        setStatus({ loading: false, success: true, error: '' });
        setName('');
        setEmail('');
        setQuestion('');
        fetchQuestions();
      } else {
        setStatus({ loading: false, success: false, error: data.error || 'Failed to submit.' });
      }
    } catch (err) {
      setStatus({ loading: false, success: false, error: 'Server unreachable. Please check backend.' });
    }
  };

  const filteredQuestions = questions.filter((item) => {
    const q = searchTerm.toLowerCase();
    return (
      item.name.toLowerCase().includes(q) ||
      item.question.toLowerCase().includes(q) ||
      (item.reply && item.reply.toLowerCase().includes(q))
    );
  });

  return (
    <div className="container py-5 min-vh-100 position-relative text-light" style={{ zIndex: 1 }}>
      
      {/* 1. Header Banner */}
      <div className="row align-items-center g-4 mb-5">
        <div className="col-12 col-lg-8" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <Sparkles size={16} className="text-cyan" />
            <span className="small text-light fw-medium">Interactive AI & Public Academic Discourse</span>
          </div>

          <h1 className="display-5 fw-bold text-white mb-3">
            Ask <span className="text-gradient">Prof. Sanjay Dhir</span>
          </h1>

          <p className="lead text-secondary fs-6 mb-4 pe-lg-3">
            Interact with <strong>AskProf</strong>, our dedicated academic assistant, for instantaneous answers about strategy, publications, and coursework. If AskProf cannot answer, submit your inquiry below for a personal response from Prof. Dhir.
          </p>

          <div className="p-3 rounded-3 bg-dark border border-secondary border-opacity-25 d-flex align-items-start gap-2 max-w-xl">
            <ShieldAlert size={18} className="text-warning flex-shrink-0 mt-1" />
            <p className="small text-secondary mb-0" style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
              <strong>Disclaimer:</strong> Responses given in this forum are based on academic learnings and professional experience. They should by no means be taken as a substitute for professional counsel.
            </p>
          </div>
        </div>

        {/* Quick Assistant Profile Card */}
        <div className="col-12 col-lg-4" data-aos="fade-left">
          <div className="p-4 rounded-4 glass-panel border-glow text-center d-flex flex-column align-items-center justify-content-center h-100">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center mb-3 shadow"
              style={{ width: '64px', height: '64px', background: 'linear-gradient(135deg, #00f2fe, #4facfe)', color: '#0a0f1d' }}
            >
              <Bot size={34} />
            </div>
            <h5 className="text-white fw-bold mb-1">Meet AskProf</h5>
            <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(0, 242, 254, 0.1)', color: '#00f2fe', border: '1px solid rgba(0, 242, 254, 0.25)', fontSize: '0.72rem' }}>
              Academic Chatbot
            </span>
            <p className="text-secondary small mb-0 lh-base">
              Trained on research publications, courses taught, patent details, and mentorship opportunities.
            </p>
          </div>
        </div>
      </div>

      {/* 2. Chatbot "AskProf" Section */}
      <div className="p-4 p-md-5 rounded-4 glass-panel mb-5" data-aos="fade-up">
        <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom border-secondary border-opacity-25">
          <div className="d-flex align-items-center gap-2">
            <div className="p-2 rounded-circle bg-dark border border-info border-opacity-50">
              <Bot size={20} className="text-cyan" />
            </div>
            <div>
              <h5 className="text-white fw-bold mb-0">AskProf AI Chatbot</h5>
              <span className="small text-success d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                <span className="rounded-circle bg-success d-inline-block" style={{ width: '7px', height: '7px' }}></span>
                Online & Ready
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setMessages([{ sender: 'bot', text: "Hello again! How can I assist you with Prof. Sanjay Dhir's academic background or research?" }])}
            className="btn btn-sm btn-outline-glass text-secondary d-flex align-items-center gap-1 px-3 py-1 rounded-pill"
            style={{ fontSize: '0.75rem' }}
          >
            <RefreshCw size={12} /> Clear Chat
          </button>
        </div>

        {/* Chat Feed */}
        <div 
          className="p-3 rounded-4 mb-3 d-flex flex-column gap-3 overflow-y-auto"
          style={{ height: '380px', background: '#0a0f1d', border: '1px solid rgba(255, 255, 255, 0.08)' }}
        >
          {messages.map((m, idx) => (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              key={idx}
              className={`d-flex ${m.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
            >
              <div 
                className={`p-3 rounded-4 max-w-lg ${m.sender === 'user' ? 'text-dark fw-medium' : 'text-light'}`}
                style={{
                  maxWidth: '80%',
                  fontSize: '0.9rem',
                  background: m.sender === 'user' 
                    ? 'linear-gradient(135deg, #00f2fe, #4facfe)' 
                    : 'rgba(30, 41, 59, 0.8)',
                  border: m.sender === 'user' ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: m.sender === 'user' ? '0 0 12px rgba(0, 242, 254, 0.3)' : 'none'
                }}
              >
                <div className="d-flex align-items-center gap-1 mb-1 small fw-bold" style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                  {m.sender === 'user' ? <User size={13} /> : <Bot size={13} />}
                  <span>{m.sender === 'user' ? 'You' : 'AskProf'}</span>
                </div>
                <div className="lh-base" style={{ whiteSpace: 'pre-line' }}>{m.text}</div>
                
                {m.isFallback && (
                  <button
                    type="button"
                    onClick={scrollToComments}
                    className="btn btn-sm btn-info text-dark fw-bold rounded-pill px-3 py-1 mt-2 d-inline-flex align-items-center gap-1"
                    style={{ fontSize: '0.75rem' }}
                  >
                    <span>Scroll to Leave a Comment</span>
                    <ArrowDown size={13} />
                  </button>
                )}
              </div>
            </motion.div>
          ))}

          {isTyping && (
            <div className="d-flex justify-content-start">
              <div className="p-3 rounded-4 bg-dark text-secondary small border border-secondary border-opacity-25">
                AskProf is looking up information...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input Bar */}
        <form onSubmit={handleChatSubmit} className="d-flex gap-2">
          <input
            type="text"
            placeholder="Ask AskProf about research, patents, teaching, or supervision..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            className="form-control rounded-pill bg-dark text-light border border-secondary border-opacity-50 px-4 py-2 small"
          />
          <button
            type="submit"
            className="btn btn-info text-dark fw-bold rounded-pill px-4 d-inline-flex align-items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #00f2fe, #4facfe)', border: 'none' }}
          >
            <Send size={15} />
            <span className="d-none d-sm-inline">Send</span>
          </button>
        </form>

        {/* Suggested Queries */}
        <div className="d-flex flex-wrap gap-2 mt-3 align-items-center">
          <span className="small text-secondary" style={{ fontSize: '0.75rem' }}>Popular queries:</span>
          {[
            'What are his key research areas?',
            'What courses does he teach?',
            'Tell me about the Scorpio patent',
            'How can I do PhD under him?'
          ].map((prompt, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setChatInput(prompt);
              }}
              className="badge bg-dark text-secondary border border-secondary border-opacity-25 px-2 py-1 text-decoration-none"
              style={{ fontSize: '0.72rem', cursor: 'pointer' }}
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>

      {/* 3. Leave a Comment / Query Form */}
      <div ref={commentSectionRef} className="row g-4 mb-5">
        <div className="col-12 col-lg-7">
          <div className="p-4 p-md-5 rounded-4 glass-panel h-100">
            <h4 className="text-white fw-bold mb-2">Leave a Comment / Direct Question</h4>
            <p className="text-secondary small mb-4">
              Can't get what you need from AskProf? Submit your question below. It will appear on this page along with Prof. Dhir's reply.
            </p>

            {status.success && (
              <div 
                className="alert alert-success d-flex align-items-center gap-2 py-3 rounded-3 mb-4"
                style={{ background: 'rgba(16, 185, 129, 0.15)', borderColor: 'rgba(16, 185, 129, 0.4)', color: '#34d399' }}
              >
                <CheckCircle2 size={18} />
                <span>Your comment has been posted successfully!</span>
              </div>
            )}

            {status.error && (
              <div 
                className="alert alert-danger d-flex align-items-center gap-2 py-3 rounded-3 mb-4"
                style={{ background: 'rgba(239, 68, 68, 0.15)', borderColor: 'rgba(239, 68, 68, 0.4)', color: '#f87171' }}
              >
                <AlertCircle size={18} />
                <span>{status.error}</span>
              </div>
            )}

            <form onSubmit={handleCommentSubmit}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Your Name *</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Aditya Mandoliya"
                    className="form-control rounded-3 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Email (Optional)</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g. aditya@institution.edu"
                    className="form-control rounded-3 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label small text-secondary">Your Question / Inquiry *</label>
                  <textarea
                    required
                    rows="4"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask about research project guidance, technology transfer courses, or case study publications..."
                    className="form-control rounded-3 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
                  />
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  disabled={status.loading}
                  className="btn btn-info text-dark fw-bold px-4 py-2 rounded-pill d-inline-flex align-items-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #00f2fe, #4facfe)', border: 'none' }}
                >
                  <Send size={16} />
                  <span>{status.loading ? 'Posting...' : 'Post Comment'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Stats & Search */}
        <div className="col-12 col-lg-5">
          <div className="p-4 p-md-5 rounded-4 glass-panel h-100 d-flex flex-column justify-content-between">
            <div>
              <h5 className="text-white fw-bold mb-3">Browse Comments</h5>
              <p className="text-secondary small mb-4">
                Search previously posted inquiries and verified responses from Prof. Sanjay Dhir.
              </p>

              <div className="position-relative mb-4">
                <Search size={18} className="text-secondary position-absolute top-50 start-0 translate-middle-y ms-3" />
                <input
                  type="text"
                  placeholder="Filter inquiries by name or topic..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control rounded-pill ps-5 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
                />
              </div>

              <div className="p-3 rounded-3 bg-dark border border-secondary border-opacity-25">
                <div className="d-flex justify-content-between text-secondary small mb-1">
                  <span>Total Inquiries:</span>
                  <strong className="text-white">{questions.length}</strong>
                </div>
                <div className="d-flex justify-content-between text-secondary small">
                  <span>Answered by Prof. Dhir:</span>
                  <strong className="text-info">{questions.filter((q) => q.reply).length}</strong>
                </div>
              </div>
            </div>

            <div className="pt-3 border-top border-secondary border-opacity-25 text-secondary small">
              Inquiries are publicly visible. Spam or off-topic promotional remarks will be removed.
            </div>
          </div>
        </div>
      </div>

      {/* 4. Display Community Comments / Feed */}
      <h4 className="text-white fw-bold mb-4">
        Community Inquiries & Responses ({filteredQuestions.length})
      </h4>

      {loading ? (
        <div className="text-center py-5">
          <p className="text-secondary fs-5">Loading comments...</p>
        </div>
      ) : filteredQuestions.length === 0 ? (
        <div className="text-center py-5 rounded-4 glass-panel">
          <p className="text-secondary fs-5 mb-0">No public inquiries found.</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-4">
          <AnimatePresence>
            {filteredQuestions.map((item) => (
              <motion.div
                layout
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.25 }}
                key={item._id}
                className="p-4 rounded-4 glass-panel position-relative overflow-hidden"
                style={{
                  borderLeft: item.reply ? '4px solid #00f2fe' : '4px solid #f59e0b'
                }}
              >
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center text-dark fw-bold"
                      style={{ width: '38px', height: '38px', background: '#38bdf8', fontSize: '14px' }}
                    >
                      {item.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h6 className="text-white fw-bold mb-0">{item.name}</h6>
                      <span className="text-secondary small" style={{ fontSize: '0.72rem' }}>
                        {new Date(item.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>

                  <span 
                    className="badge rounded-pill small"
                    style={{
                      background: item.reply ? 'rgba(0, 242, 254, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                      color: item.reply ? '#00f2fe' : '#f59e0b',
                      border: item.reply ? '1px solid rgba(0, 242, 254, 0.3)' : '1px solid rgba(245, 158, 11, 0.3)',
                    }}
                  >
                    {item.reply ? 'Answered' : 'Pending Response'}
                  </span>
                </div>

                <p className="text-light opacity-90 my-3 ps-2 lh-base" style={{ fontSize: '0.95rem' }}>
                  {item.question}
                </p>

                {item.reply && (
                  <div 
                    className="mt-3 p-3 rounded-3"
                    style={{ 
                      background: 'rgba(2, 6, 23, 0.65)', 
                      border: '1px solid rgba(0, 242, 254, 0.25)',
                      marginLeft: '1.5rem'
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <CornerDownRight size={14} className="text-info" />
                      <strong className="text-info small">{item.repliedBy || 'Prof. Sanjay Dhir'}</strong>
                      {item.repliedAt && (
                        <span className="text-secondary" style={{ fontSize: '0.7rem' }}>
                          • {new Date(item.repliedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <p className="text-light small mb-0 ps-3 lh-base">
                      {item.reply}
                    </p>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

    </div>
  );
}